import type { Guide, KbSection } from './types';
import { chequeGuides } from './cheques';
import { apPaymentGuides } from './apPayments';
import { accountLocationGuides } from './accountLocations';
import { chequePrintingGuides } from './chequePrinting';
import { allocationAgeingGuides } from './allocationsAgeing';

export * from './types';

/**
 * The knowledge base, section by section.
 *
 * Sections mirror the menu, because "where do I find it?" is the question a reader actually has.
 * Add a section here and its guides in a file of their own; the index page counts and groups them
 * without further wiring.
 */
export const KB_SECTIONS: KbSection[] = [
  {
    key: 'ap-payments',
    title: 'Accounts Payable — Payments',
    icon: 'Wallet',
    description: 'Paying suppliers: funding accounts, settling bills, approval, posting and reversals.',
  },
  {
    key: 'account-locations',
    title: 'Accounts & Locations',
    icon: 'MapPin',
    description: 'Tying a GL account to the locations that may use it, and reading the refusals.',
  },
  {
    key: 'allocations-ageing',
    title: 'Allocations & Ageing',
    icon: 'Scale',
    description: 'Settling documents from inside a voucher, and the ageing bands that show what is left.',
  },
  {
    key: 'cheque-printing',
    title: 'Cheque Printing',
    icon: 'Printer',
    description: 'The print run: crossing, payee, rubber stamp, confirmation and reprints.',
  },
  {
    key: 'cheques',
    title: 'Cheques',
    icon: 'BookCheck',
    description: 'Cheque books, layouts, paying by cheque, printing, hand-over and the register.',
  },
];

export const KB_GUIDES: Guide[] = [
  ...apPaymentGuides,
  ...accountLocationGuides,
  ...allocationAgeingGuides,
  ...chequePrintingGuides,
  ...chequeGuides,
];

export const guideBySlug = (slug: string): Guide | undefined =>
  KB_GUIDES.find(g => g.slug === slug);

export const guidesInSection = (key: string): Guide[] =>
  KB_GUIDES.filter(g => g.section === key);

/**
 * Everything in a guide that a reader might search for, flattened to one lowercase string.
 *
 * Includes the body, not just the title: someone searching "bounced" or "payable to CASH" is
 * looking for a sentence inside a guide, and matching titles alone would find nothing.
 */
export const guideSearchText = (g: Guide): string => {
  const parts: string[] = [g.title, g.summary, g.menuPath, g.section, g.level];

  for (const b of g.blocks) {
    switch (b.kind) {
      case 'para':
      case 'heading':  parts.push(b.text); break;
      case 'callout':  parts.push(b.title ?? '', b.text); break;
      case 'list':     parts.push(...b.items); break;
      case 'steps':    parts.push(...b.steps.flatMap(s => [s.title, s.detail ?? '', s.where ?? ''])); break;
      case 'table':    parts.push(...b.headers, ...b.rows.flat()); break;
      case 'links':    parts.push(b.title ?? '', ...b.links.map(l => l.label)); break;
      case 'image':    parts.push(b.caption ?? ''); break;
    }
  }

  return parts.join(' ').toLowerCase();
};
